import { useState } from "react";
import Title from "../typography/Title";
import Subtitle from "../typography/Subtitle";
import Button from "../../shared/button/Button";
import {
  CheckCircle,
  ArrowUpRight,
  Minus,
  Plus,
  MagnifyingGlass,
  VideoCamera,
} from "@phosphor-icons/react/dist/ssr";
import { twJoin } from "tailwind-merge";
import { Link } from "react-router-dom";

// ─── Constants ──────────────────────────────────────────────────────────────
const CALENDLY_URL =
  "https://calendly.com/sales-babblebots/babblebots-discovery";
const SIGNUP_URL = "https://app.babblebots.ai/signup";

// Single credit-based pricing (replaces the old per-module AI Screening /
// AI Talent Discovery / Campus Drives / API tabs, retired 2026-08-30).
// Both plans share the same per-seat rate — Starter's rate is ₹7,500/500
// credits = ₹15/credit; Team's is ₹15,000/1,500 credits = ₹10/credit, a flat
// 33% better credit rate at any seat count (not a bulk-seat discount).
const PRICE_PER_SEAT_INR = 7500;
const CREDITS_PER_SEAT = 750;
const STARTER_CREDITS = 500;
const MIN_TEAM_SEATS = 2;
const MAX_TEAM_SEATS = 5;
const TRIAL_CREDITS = 50;

const ACCENT = {
  primary: "#41C3D5",
  light: "rgba(65,195,213,0.06)",
  mid: "rgba(65,195,213,0.15)",
  glow: "rgba(65,195,213,0.25)",
};

// ─── Shared: Feature Item ───────────────────────────────────────────────────
const FeatureItem = ({ text }: { text: string }) => (
  <div className="flex items-start gap-3 py-1.5">
    <CheckCircle
      weight="fill"
      className="mt-0.5 h-5 w-5 flex-shrink-0"
      style={{ color: ACCENT.primary }}
    />
    <span className="text-sm leading-relaxed text-gray-250">{text}</span>
  </div>
);

// Grouped by AI agent rather than one long flat list, so it's clear which
// features come from the sourcing/search side vs. the screening side.
const sourcingFeatures = [
  "Unlimited Searches across 90M+ Profiles",
  "Unlimited Profile Downloads (PDF)",
  "Unlimited Roles",
  "Contact Email and Phone Number",
];

const screeningFeatures = [
  "AI-Video Interviews",
  "AI-Phone Screening",
  "Coding Tests",
  "Aptitude Tests",
  "Psychometric Tests",
];

const trialFeatures = [
  "Unlimited Roles",
  "Unlimited Searches",
  "All AI-Screening Modules",
];

const enterpriseFeatures = [
  "Branded Email & WhatsApp Outreach",
  "Team Management Features",
  "Dedicated Account Manager",
  "Custom Assessments",
  "Integration with ATS / HRMS",
  "Compliance Readiness",
  "Priority Support — Email, Chat & Phone",
];

// ─── Shared: Feature Group (grouped under an AI agent label) ───────────────
const FeatureGroup = ({
  icon: Icon,
  label,
  items,
}: {
  icon: typeof MagnifyingGlass;
  label: string;
  items: string[];
}) => (
  <div className="mb-4 last:mb-0">
    <div className="mb-2 flex items-center gap-1.5">
      <Icon size={14} weight="bold" style={{ color: ACCENT.primary }} />
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
        {label}
      </p>
    </div>
    <div className="flex flex-col gap-0.5">
      {items.map((item) => (
        <FeatureItem key={item} text={item} />
      ))}
    </div>
  </div>
);

// ─── Shared: Plan Stat (Users / Credits chip) ───────────────────────────────
const PlanStat = ({ value, label }: { value: string; label: string }) => (
  <div className="flex-1 rounded-lg bg-white/[0.04] px-3 py-2 text-center">
    <p className="text-sm font-semibold text-white">{value}</p>
    <p className="text-xs text-gray-400">{label}</p>
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────
const Packages = () => {
  const [teamSeats, setTeamSeats] = useState(MIN_TEAM_SEATS);
  const teamPrice = teamSeats * PRICE_PER_SEAT_INR;
  const teamCredits = teamSeats * CREDITS_PER_SEAT;

  return (
    <section className="relative px-4 pt-14 sm:px-6 md:px-12 md:pt-20 lg:px-16 lg:pt-24">
      {/* ── Hero ── */}
      <div className="mx-auto mb-16 max-w-4xl text-center">
        <Subtitle>BABBLEBOTS AI PRICING</Subtitle>
        <Title as="h1" size="lg" className="mt-4">
          Every step of hiring. <br />Powered by Babblebots AI Credits.
        </Title>
      </div>

      {/* ── Plan Cards ── */}
      <div
        className="mx-auto max-w-7xl rounded-3xl border bg-gray-850/60 p-6 backdrop-blur-sm md:p-10 lg:p-12"
        style={{
          borderColor: `${ACCENT.primary}18`,
          boxShadow: `0 0 80px -20px ${ACCENT.light}, inset 0 1px 0 ${ACCENT.mid}`,
        }}
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
          {/* ── Free Trial ── */}
          <div className="rounded-2xl bg-white/[0.08] p-px hover:bg-white/[0.12]">
            <div className="flex h-full flex-col rounded-2xl bg-gray-850 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-serif text-2xl font-bold text-white">Free Trial</h3>
                <span className="rounded-full px-2.5 py-1 text-xs font-semibold text-gray-200" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
                  7 DAYS
                </span>
              </div>

              <div className="mt-5 border-t border-white/[0.06] pt-5">
                <span className="text-3xl font-bold" style={{ color: ACCENT.primary }}>
                  7-Day Trial
                </span>
              </div>

              <div className="mt-5 flex gap-2">
                <PlanStat value="1" label="User" />
                <PlanStat value={TRIAL_CREDITS.toLocaleString("en-IN")} label="AI-Credits" />
              </div>

              <div className="mt-5 flex flex-1 flex-col gap-0.5 border-t border-white/[0.06] pt-5">
                {trialFeatures.map((feat) => (
                  <FeatureItem key={feat} text={feat} />
                ))}
              </div>

              <Link to={SIGNUP_URL} className="mt-6 block">
                <Button className="w-full border-white/[0.08] bg-white/[0.05] text-white hover:bg-white/[0.1]">
                  Start Trial
                </Button>
              </Link>
            </div>
          </div>

          {/* ── Starter ── */}
          <div className="rounded-2xl bg-white/[0.08] p-px hover:bg-white/[0.12]">
            <div className="flex h-full flex-col rounded-2xl bg-gray-850 p-6">
              <h3 className="font-serif text-2xl font-bold text-white">Starter</h3>

              <div className="mt-5 border-t border-white/[0.06] pt-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold" style={{ color: ACCENT.primary }}>
                    ₹7,500
                  </span>
                  <span className="text-xs text-gray-500">/ month + GST</span>
                </div>
              </div>

              <div className="mt-5 flex gap-2">
                <PlanStat value="1" label="User" />
                <PlanStat value={STARTER_CREDITS.toLocaleString("en-IN")} label="AI-Credits" />
              </div>

              <div className="mt-5 flex flex-1 flex-col border-t border-white/[0.06] pt-5">
                <FeatureGroup icon={MagnifyingGlass} label="AI Talent Sourcing Agent" items={sourcingFeatures} />
                <FeatureGroup icon={VideoCamera} label="AI Screening Agent" items={screeningFeatures} />
              </div>

              <Link to={SIGNUP_URL} className="mt-6 block">
                <Button className="w-full border-white/[0.08] bg-white/[0.05] text-white hover:bg-white/[0.1]">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>

          {/* ── Team (recommended) ── */}
          <div
            className="rounded-2xl p-px"
            style={{ background: `linear-gradient(180deg, ${ACCENT.primary}80, ${ACCENT.primary}20)` }}
          >
            <div className="relative flex h-full flex-col rounded-2xl bg-gray-850 p-6">
              <span
                className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #FF9500, #FF6B00)" }}
              >
                RECOMMENDED
              </span>

              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-serif text-2xl font-bold text-white">Team</h3>
                <span className="rounded-full px-2.5 py-1 text-xs font-semibold text-emerald-300" style={{ backgroundColor: "rgba(52,211,153,0.12)" }}>
                  SAVE 33%
                </span>
              </div>

              <div className="mt-5 border-t border-white/[0.06] pt-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold" style={{ color: ACCENT.primary }}>
                    ₹{teamPrice.toLocaleString("en-IN")}
                  </span>
                  <span className="text-xs text-gray-500">/ month + GST</span>
                </div>
              </div>

              {/* ── Users stepper + Credits stat ── */}
              <div className="mt-5 flex gap-2">
                <div className="flex flex-1 items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2">
                  <button
                    type="button"
                    aria-label="Decrease users"
                    onClick={() => setTeamSeats((s) => Math.max(MIN_TEAM_SEATS, s - 1))}
                    disabled={teamSeats <= MIN_TEAM_SEATS}
                    className={twJoin(
                      "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-white/[0.1] text-white transition-colors",
                      teamSeats <= MIN_TEAM_SEATS ? "opacity-40" : "hover:bg-white/[0.08]"
                    )}
                  >
                    <Minus size={12} weight="bold" />
                  </button>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-white">{teamSeats}</p>
                    <p className="text-xs text-gray-400">
                      Users ({MIN_TEAM_SEATS}–{MAX_TEAM_SEATS})
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label="Increase users"
                    onClick={() => setTeamSeats((s) => Math.min(MAX_TEAM_SEATS, s + 1))}
                    disabled={teamSeats >= MAX_TEAM_SEATS}
                    className={twJoin(
                      "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-white/[0.1] text-white transition-colors",
                      teamSeats >= MAX_TEAM_SEATS ? "opacity-40" : "hover:bg-white/[0.08]"
                    )}
                  >
                    <Plus size={12} weight="bold" />
                  </button>
                </div>
                <PlanStat value={teamCredits.toLocaleString("en-IN")} label={`AI-Credits (${CREDITS_PER_SEAT}/user)`} />
              </div>

              <div className="mt-5 flex flex-1 flex-col border-t border-white/[0.06] pt-5">
                <FeatureGroup icon={MagnifyingGlass} label="AI Talent Sourcing Agent" items={sourcingFeatures} />
                <FeatureGroup icon={VideoCamera} label="AI Screening Agent" items={screeningFeatures} />
              </div>

              <Link to={SIGNUP_URL} className="mt-6 block">
                <button
                  className="w-full rounded-lg px-4 py-2.5 text-sm font-bold text-gray-900 transition-all duration-300 hover:brightness-110 active:scale-[0.98]"
                  style={{ backgroundColor: ACCENT.primary, boxShadow: `0 4px 20px ${ACCENT.glow}` }}
                >
                  Get Started
                </button>
              </Link>
            </div>
          </div>

          {/* ── Enterprise ── */}
          <div className="rounded-2xl bg-white/[0.08] p-px hover:bg-white/[0.12]">
            <div className="flex h-full flex-col rounded-2xl bg-gray-850 p-6">
              <h3 className="font-serif text-2xl font-bold text-white">Enterprise</h3>

              <div className="mt-5 border-t border-white/[0.06] pt-5">
                <span className="text-3xl font-bold" style={{ color: ACCENT.primary }}>
                  Custom
                </span>
              </div>

              <p
                className="mt-5 border-t border-white/[0.06] pt-4 text-xs font-bold uppercase tracking-wider"
                style={{ color: ACCENT.primary, opacity: 0.75 }}
              >
                Bespoke solution — everything in Team, plus
              </p>
              <div className="mt-4 flex flex-1 flex-col gap-0.5">
                {enterpriseFeatures.map((feat) => (
                  <FeatureItem key={feat} text={feat} />
                ))}
              </div>

              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 block"
              >
                <button
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-white/[0.1]"
                >
                  Book a Meeting
                  <ArrowUpRight size={16} weight="bold" />
                </button>
              </a>
            </div>
          </div>
        </div>

        {/* ── Discontinued modules note ── */}
        <p className="mt-8 text-center text-sm text-gray-400">
          Looking for Campus Drives or API access? Those are no longer self-serve —{" "}
          <Link
            to="/contactus"
            className="font-semibold underline decoration-white/30 underline-offset-2 transition-colors hover:text-white"
            style={{ color: ACCENT.primary }}
          >
            write to us directly
          </Link>{" "}
          and we'll help.
        </p>
      </div>
    </section>
  );
};

export default Packages;
